/* eslint-disable */
/**
 * Reglas y datos de la vista de análisis de causas.
 *
 * Estos métodos operan sobre la instancia de PanelLogic y conservan el
 * contrato del prototipo importado. No mantienen estado global propio.
 */
const logic = {
  get czMeta() {
    return {
      cur: {
        l: 'Corriente',
        u: 'cm/s',
        dp: 0
      },
      tasa: {
        l: 'Tasa',
        u: 'nivel',
        dp: 0,
        disc: true
      },
      dep: {
        l: 'Profundidad',
        u: 'm',
        dp: 0
      },
      temp: {
        l: 'Temperatura',
        u: '°C',
        dp: 1
      },
      oxy: {
        l: 'Oxígeno',
        u: '%',
        dp: 0
      }
    };
  },
  czTest(p, conds) {
    return conds.every(c => c.op === '>=' ? p[c.k] >= c.b : c.op === '<' ? p[c.k] < c.b : p[c.k] === c.b);
  },
  czVals(st) {
    const D = this.data,
      N = D.days.length,
      M = this.czMeta,
      keys = Object.keys(M),
      per = st.czPer || 'all',
      scope = st.czScope || 'all',
      outK = st.czOut || 'ppa';
    const d0 = per === 'all' ? 0 : Math.max(0, st.day - per + 1),
      d1 = per === 'all' ? N - 1 : st.day;
    const P = this.czPts().filter(p => p.d >= d0 && p.d <= d1 && (scope === 'all' || p.ci === st.sel)),
      n = P.length;
    if (!n) return {
      czEmpty: true,
      czHas: false,
      czTip: {
        on: false,
        rows: []
      }
    };
    // The prototype only used integer feeding-rate levels, but production data
    // can contain a continuous kg/min series. Treating every real value as a
    // category creates hundreds of columns and unreadable, overlapping labels.
    // Keep the level presentation only when the data genuinely looks discrete.
    const rateValues = [...new Set(P.map(p => p.tasa).filter(Number.isFinite))],
      rateIsDiscrete = rateValues.length > 0 && rateValues.length <= 12 && rateValues.every(v => Math.abs(v - Math.round(v)) < 1e-9);
    M.tasa = {
      ...M.tasa,
      disc: rateIsDiscrete,
      u: rateIsDiscrete ? 'nivel' : 'kg/min'
    };
    const y = p => outK === 'ppa' ? p.ppa : p.ppa / (p.tasa * 5),
      tot = P.reduce((s, p) => s + y(p), 0),
      mean = n ? tot / n : 0,
      lossT = P.reduce((s, p) => s + p.ppa, 0);
    const dpK = {};
    keys.forEach(k => {
      if (M[k].disc) return;
      const vs = P.map(p => p[k]),
        w = (Math.max(...vs) - Math.min(...vs)) / 10;
      dpK[k] = w < 0.1 ? 2 : w < 1 ? 1 : 0;
    });
    const fmtB = (k, v) => M[k].disc ? String(v) : v.toFixed(dpK[k] ?? M[k].dp);
    const binsOf = k => {
      if (M[k].disc) {
        const L = [...new Set(P.map(p => p[k]))].sort((a, b) => a - b);
        return L.map(v => ({
          a: v,
          b: v,
          lab: 'nivel ' + v,
          test: p => p[k] === v
        }));
      }
      const vs = P.map(p => p[k]),
        lo = Math.min(...vs),
        hi = Math.max(...vs),
        w = (hi - lo) / 10 || 1;
      return [...Array(10)].map((_, j) => {
        const a = lo + j * w,
          b = j === 9 ? hi + 1e-9 : a + w;
        return {
          a,
          b,
          lab: fmtB(k, a) + '–' + fmtB(k, b),
          test: p => p[k] >= a && p[k] < b
        };
      });
    };
    const stat = test => {
      let c = 0,
        s = 0,
        l = 0;
      for (const p of P) if (test(p)) {
        c++;
        s += y(p);
        l += p.ppa;
      }
      return {
        c,
        m: c ? s / c : 0,
        l
      };
    };
    const ruleOf = k => {
      const B = binsOf(k);
      let best = null;
      if (M[k].disc) B.forEach(b => {
        const s = stat(b.test);
        if (s.c / n >= 0.08 && mean && (!best || s.m / mean > best.lift)) best = {
          conds: [{
            k,
            op: '==',
            b: b.a
          }],
          lift: s.m / mean,
          s,
          txt: M[k].l + ' nivel ' + b.a
        };
      });else for (let j = 1; j < B.length; j++) {
        const e = B[j].a;
        ['>=', '<'].forEach(op => {
          const cs = [{
              k,
              op,
              b: e
            }],
            s = stat(p => this.czTest(p, cs));
          if (s.c / n >= 0.08 && mean && (!best || s.m / mean > best.lift)) best = {
            conds: cs,
            lift: s.m / mean,
            s,
            txt: M[k].l + (op === '>=' ? ' > ' : ' < ') + fmtB(k, e) + (M[k].u ? ' ' + M[k].u : '')
          };
        });
      }
      return best;
    };
    const R = keys.map(k => ({
        k,
        r: ruleOf(k)
      })).filter(x => x.r).sort((a, b) => b.r.lift - a.r.lift),
      drv = st.czVar && M[st.czVar] ? st.czVar : R[0].k,
      mxL = Math.max(...R.map(x => x.r.lift));
    const czDrivers = R.map(x => ({
      l: M[x.k].l,
      lift: x.r.lift.toFixed(1) + '×',
      w: ((x.r.lift - 1) / Math.max(0.01, mxL - 1) * 100).toFixed(1) + '%',
      sub: x.r.txt.replace(M[x.k].l + ' ', ''),
      on: x.k === drv,
      bg: x.k === drv ? 'rgba(46,179,238,0.14)' : 'transparent',
      bd: x.k === drv ? 'rgba(46,179,238,0.45)' : 'transparent',
      c: x.r.lift >= 2 ? 'oklch(0.7 0.18 28)' : x.r.lift >= 1.4 ? 'oklch(0.8 0.15 75)' : 'oklch(0.75 0.11 190)',
      onClick: () => this.setState({
        czVar: x.k,
        czHov: null,
        czGH: null
      })
    }));
    // curve
    const B = binsOf(drv),
      split = !!st.czSplit && drv !== 'tasa',
      lv = [...new Set(P.map(p => p.tasa))].sort((a, b) => a - b),
      LC = {
        2: '#1f8f8c',
        3: '#d98b3f',
        4: '#e0577f'
      };
    const bs = B.map(b => ({
        ...b,
        ...stat(b.test)
      })),
      yMx = Math.max(mean * 1.3, ...bs.filter(b => b.c / n >= 0.01).map(b => b.m)) * 1.12,
      tMx = Math.max(...bs.map(b => b.c / n)) * 1.15,
      nb = bs.length,
      bw = 600 / nb;
    const Y = v => (100 - v / yMx * 100).toFixed(2),
      cx = j => ((j + 0.5) * bw).toFixed(1);
    const czTime = bs.map((b, j) => 'M' + (j * bw + bw * 0.12).toFixed(1) + ' 100V' + (100 - b.c / n / tMx * 100 * 0.45).toFixed(2) + 'h' + (bw * 0.76).toFixed(1) + 'V100Z').join('');
    const pts = bs.map((b, j) => b.c / n >= 0.005 ? cx(j) + ' ' + Y(b.m) : null);
    const pathOf = arr => {
      let s = '',
        pen = false;
      arr.forEach(p => {
        if (p) {
          s += (pen ? ' L' : ' M') + p;
          pen = true;
        } else pen = false;
      });
      return s.trim() || 'M0 0';
    };
    const czLines = split ? lv.map(l => ({
      d: pathOf(B.map((b, j) => {
        const s = stat(p => p.tasa === l && b.test(p));
        return s.c >= 8 ? cx(j) + ' ' + Y(s.m) : null;
      })),
      c: LC[l] || '#e4e9f0',
      w: '2'
    })) : [{
      d: pathOf(pts),
      c: 'oklch(0.72 0.16 350)',
      w: '2.5'
    }];
    const czDots = split ? [] : bs.map((b, j) => b.c / n >= 0.005 ? {
      x: (j + 0.5) / nb * 100 + '%',
      y: Y(b.m) + '%',
      c: b.m / mean >= 2 ? 'oklch(0.7 0.18 28)' : b.m / mean >= 1.4 ? 'oklch(0.8 0.15 75)' : 'oklch(0.72 0.16 350)'
    } : null).filter(Boolean);
    const rD = R.find(x => x.k === drv).r,
      c0 = rD.conds[0];
    const thrX = M[drv].disc ? null : (c0.b - B[0].a) / (B[nb - 1].b - B[0].a) * 100;
    const hv = st.czHov != null && st.czHov < nb ? bs[st.czHov] : null;
    const czTip = hv ? {
      on: true,
      L: ((st.czHov + 0.5) / nb * 100).toFixed(2) + '%',
      tx: st.czHov / nb > 0.6 ? "translate(calc(-100% - 0.520833vw),0)" : "translate(0.520833vw,0)",
      title: M[drv].l + ' ' + hv.lab + (M[drv].disc || !M[drv].u ? '' : ' ' + M[drv].u),
      rows: [{
        l: outK === 'ppa' ? 'PPA prom. / 5 min' : 'PPA por kg',
        v: hv.m.toFixed(outK === 'ppa' ? 2 : 3)
      }, {
        l: 'vs promedio',
        v: mean ? (hv.m / mean).toFixed(2) + '×' : '–'
      }, {
        l: '% del tiempo alimentando',
        v: (hv.c / n * 100).toFixed(1) + ' %'
      }, {
        l: '% de la pérdida',
        v: lossT ? (hv.l / lossT * 100).toFixed(1) + ' %' : '–'
      }, {
        l: 'Intervalos',
        v: hv.c.toLocaleString('es-CL')
      }]
    } : {
      on: false,
      rows: []
    };
    // grid
    const k2 = st.czVar2 && st.czVar2 !== drv && M[st.czVar2] ? st.czVar2 : drv === 'tasa' ? 'cur' : 'tasa',
      B2 = binsOf(k2),
      B1 = M[drv].disc ? B : binsOf(drv).filter((_, j) => true);
    const G = B2.map(b2 => B1.map(b1 => stat(p => b2.test(p) && b1.test(p)))),
      gMx = Math.max(...G.flat().filter(s => s.c >= 8).map(s => s.m), 1e-9);
    const gh = st.czGH;
    const czGrid = B2.slice().reverse().map((b2, rr) => {
      const ri = B2.length - 1 - rr;
      return {
        lab: M[k2].disc ? 'niv. ' + b2.a : fmtB(k2, b2.a),
        cells: B1.map((b1, j) => {
          const s = G[ri][j],
            ok = s.c >= 8,
            k = ok ? s.m / gMx : 0,
            on = gh && gh.r === ri && gh.j === j;
          return {
            bg: !ok ? s.c ? "repeating-linear-gradient(135deg,#1c2636 0 0.185185vh,transparent 0.185185vh 0.462963vh)" : 'transparent' : 'oklch(' + (0.3 + 0.42 * k).toFixed(3) + ' ' + (0.04 + 0.16 * k).toFixed(3) + ' ' + (190 - 165 * k).toFixed(0) + ')',
            ol: on ? "0.092593vh solid #e4e9f0" : 'none',
            enter: () => this.setState({
              czGH: {
                r: ri,
                j
              }
            })
          };
        })
      };
    });
    const ghS = gh && G[gh.r] && G[gh.r][gh.j] ? G[gh.r][gh.j] : null;
    const czGInfo = ghS ? M[drv].l + ' ' + B1[gh.j].lab + ' · ' + M[k2].l + ' ' + B2[gh.r].lab + ' → ' + (ghS.c >= 8 ? ghS.m.toFixed(2) + ' (' + (ghS.m / mean).toFixed(1) + '×) · ' + ghS.c + ' int.' : 'pocos datos') : '';
    const czV2 = keys.filter(k => k !== drv).map(k => ({
      l: M[k].l,
      bg: k === k2 ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: k === k2 ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState({
        czVar2: k,
        czGH: null
      })
    }));
    // rules
    const top = R.slice(0, 2),
      comb = top.length === 2 ? (() => {
        const cs = [...top[0].r.conds, ...top[1].r.conds],
          s = stat(p => this.czTest(p, cs));
        return s.c >= 20 ? {
          conds: cs,
          lift: s.m / mean,
          s,
          txt: top[0].r.txt + ' y ' + top[1].r.txt.charAt(0).toLowerCase() + top[1].r.txt.slice(1)
        } : null;
      })() : null;
    const rules = [...(comb && comb.lift > top[0].r.lift ? [comb] : []), ...R.slice(0, 4).map(x => x.r)].slice(0, 4);
    const act = st.czRule,
      open = st.czOpen;
    const czRules = rules.map((ru, q) => {
      const id = JSON.stringify(ru.conds),
        on = act && act.id === id,
        cas = {};
      P.forEach(p => {
        if (this.czTest(p, ru.conds)) {
          const kk = p.ci + '-' + p.d;
          (cas[kk] = cas[kk] || {
            ci: p.ci,
            d: p.d,
            c: 0,
            l: 0
          }).c++;
          cas[kk].l += p.ppa;
        }
      });
      const cs = Object.values(cas).sort((a, b) => b.l - a.l).slice(0, 4);
      return {
        txt: ru.txt,
        lift: ru.lift.toFixed(1) + '×',
        c: ru.lift >= 2 ? 'oklch(0.7 0.18 28)' : ru.lift >= 1.4 ? 'oklch(0.8 0.15 75)' : 'oklch(0.75 0.11 190)',
        time: (ru.s.c / n * 100).toFixed(0) + ' % del tiempo',
        loss: lossT ? (ru.s.l / lossT * 100).toFixed(0) + ' % de la pérdida' : '',
        markL: on ? 'Marcado ✓' : 'Marcar en Detalle',
        markBg: on ? 'oklch(0.66 0.19 42)' : 'transparent',
        markC: on ? '#fff' : '#b8c1ce',
        bd: on ? 'oklch(0.66 0.19 42)' : 'rgba(46,179,238,0.16)',
        mark: () => this.setState({
          czRule: on ? null : {
            id,
            conds: ru.conds,
            txt: ru.txt
          }
        }),
        casesOn: open === q,
        casesL: open === q ? 'Ocultar casos' : 'Ver casos',
        toggle: () => this.setState({
          czOpen: open === q ? null : q
        }),
        cases: cs.map(x => ({
          t: D.rows[x.ci].name + ' · ' + D.days[x.d],
          v: Math.round(x.l) + ' PPA · ' + x.c * 5 + ' min',
          go: () => this.setState({
            view: 'panel',
            simple: false,
            sel: x.ci,
            day: x.d,
            czRule: {
              id,
              conds: ru.conds,
              txt: ru.txt
            }
          })
        }))
      };
    });
    const seg = (list, cur, f) => list.map(([k, label]) => ({
      label,
      bg: cur === k ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: cur === k ? '#e4e9f0' : '#8793a6',
      fw: cur === k ? 600 : 400,
      onClick: () => this.setState({
        [f]: k,
        czHov: null,
        czGH: null,
        czOpen: null
      })
    }));
    const yt = [0, 0.5, 1].map(f => ({
      lab: (yMx / 1.12 * f).toFixed(outK === 'ppa' ? 1 : 2),
      top: Y(yMx / 1.12 * f) + '%'
    }));
    const czXTitle = M[drv].l + (M[drv].disc ? ' (nivel)' : M[drv].u ? ' [' + M[drv].u + ']' : ''),
      czYTitle = outK === 'ppa' ? 'PPA promedio por intervalo de 5 min' : 'PPA por kg entregado';
    const czTR = [{
      lab: '0 %',
      top: '100%'
    }, {
      lab: (tMx * 0.45 * 100).toFixed(0) + ' %',
      top: '55%'
    }];
    return {
      czXTitle,
      czYTitle,
      czTR,
      czGMax: gMx.toFixed(outK === 'ppa' ? 1 : 2),
      czGOut: outK === 'ppa' ? 'PPA prom.' : 'PPA/kg',
      czEmpty: false,
      czHas: true,
      czDrivers,
      czTime,
      czLines,
      czDots,
      czTip,
      czGrid,
      czGridCols: B1.length,
      czGInfo,
      czV2,
      czRules,
      czYt: yt,
      czMeanT: Y(mean) + '%',
      czMeanL: 'prom. ' + mean.toFixed(outK === 'ppa' ? 2 : 3),
      czThrOn: thrX != null,
      czThrL: (thrX || 0).toFixed(2) + '%',
      czThrT: rD.txt.replace(M[drv].l + ' ', '') + ' → ' + rD.lift.toFixed(1) + '×',
      czThrTx: c0.op === '<' ? "translateX(calc(-100% - 0.3125vw))" : "translateX(0.3125vw)",
      czX: bs.map((b, j) => ({
        lab: M[drv].disc ? b.lab : fmtB(drv, b.a),
        left: ((j + (M[drv].disc ? 0.5 : 0)) / nb * 100).toFixed(2) + '%'
      })),
      czXU: M[drv].disc ? '' : M[drv].u,
      czHits: bs.map((_, j) => ({
        enter: () => this.setState({
          czHov: j
        })
      })),
      czLeave: () => this.setState({
        czHov: null
      }),
      czGLeave: () => this.setState({
        czGH: null
      }),
      czTitle: (outK === 'ppa' ? 'PPA' : 'PPA por kg') + ' según ' + M[drv].l.toLowerCase(),
      czSub: n.toLocaleString('es-CL') + ' intervalos de 5 min alimentando · ' + (scope === 'all' ? D.rows.length + (this.props.nivelSeleccionado && this.props.nivelSeleccionado !== 'centro' ? ' centros' : ' jaulas') : D.rows[st.sel].name) + ' · ' + D.days[d0] + ' – ' + D.days[d1],
      czSplitOn: split,
      czNoSplit: !split,
      czSplitCan: drv !== 'tasa',
      czSplitL: split ? 'Separado por tasa ✓' : 'Separar por tasa',
      czSplitTog: () => this.setState(s => ({
        czSplit: !s.czSplit
      })),
      czLeg: split ? lv.map(l => ({
        l: 'Tasa nivel ' + l,
        c: LC[l] || '#e4e9f0'
      })) : [],
      czGT: M[drv].l + ' × ' + M[k2].l,
      czGX: M[drv].l + (M[drv].disc || !M[drv].u ? '' : ' [' + M[drv].u + ']'),
      czGY: M[k2].l + (M[k2].disc || !M[k2].u ? '' : ' [' + M[k2].u + ']'),
      czGXL: B1.map((b, j) => ({
        lab: j % 2 === 0 || M[drv].disc ? M[drv].disc ? String(b.a) : fmtB(drv, b.a) : ''
      })),
      czScopeO: seg([['all', 'Todas las jaulas'], ['one', D.rows[st.sel].name]], scope, 'czScope'),
      czPerO: seg([[7, '7 d'], [30, '30 d'], ['all', 'Todo']], per, 'czPer'),
      czOutO: seg([['ppa', 'PPA'], ['ppk', 'PPA por kg']], outK, 'czOut')
    };
  },
  czSpans(ci, di, rule) {
    const pts = this.czPts().filter(p => p.ci === ci && p.d === di && this.czTest(p, rule.conds));
    if (!pts.length) return {
      d: 'M0 0',
      n: 0
    };
    const X = i => i / 180 * 600,
      hw = 300 / 180;
    let s = '',
      a = null,
      b = null;
    pts.forEach(p => {
      if (a == null) {
        a = p.i;
        b = p.i;
      } else if (p.i === b + 1) b = p.i;else {
        s += 'M' + Math.max(0, X(a) - hw).toFixed(1) + ' 0 H' + Math.min(600, X(b) + hw).toFixed(1) + ' V100 H' + Math.max(0, X(a) - hw).toFixed(1) + ' Z ';
        a = p.i;
        b = p.i;
      }
    });
    s += 'M' + Math.max(0, X(a) - hw).toFixed(1) + ' 0 H' + Math.min(600, X(b) + hw).toFixed(1) + ' V100 H' + Math.max(0, X(a) - hw).toFixed(1) + ' Z';
    return {
      d: s,
      n: pts.length
    };
  }
};
export default logic;
